import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  FaPlay,
  FaPause,
  FaVolumeHigh,
  FaVolumeLow,
  FaVolumeXmark,
  FaExpand,
  FaCompress,
} from "react-icons/fa6";
import api from "../utils/axiosConfig";
import {
  CourseVideoView,
  VideoProgressResponse,
} from "../utils/entities/course";

interface Props {
  courseId: number;
  lessonId: number;
  video: CourseVideoView;
  onCompleted: (videoId: number) => void;
  /** Esconde o badge de status (status fica no cabeçalho da aula). */
  compactStatus?: boolean;
}

const SEEK_TOLERANCE = 1.5;
const HEARTBEAT_MS = 10000;

const formatTime = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

const CourseVideoPlayer: React.FC<Props> = ({
  courseId,
  lessonId,
  video,
  onCompleted,
  compactStatus = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const watchedUpToRef = useRef<number>(video.maxWatchedSeconds || 0);
  const notifiedLessonCompleteRef = useRef(false);
  const seekingProgrammaticallyRef = useRef(false);
  const playStartedAtRef = useRef<number>(0);
  const progressInFlightRef = useRef(false);
  const pendingEndedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [completed, setCompleted] = useState(video.completed);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    watchedUpToRef.current = video.maxWatchedSeconds || 0;
    setCompleted(video.completed);
  }, [video.id, video.maxWatchedSeconds, video.completed]);

  useEffect(() => {
    notifiedLessonCompleteRef.current = false;
  }, [video.id, lessonId]);

  // Presigned URLs rotacionam no refetch; o <video> precisa de load() para
  // trocar de fonte sem ficar em tela preta com o mesmo video.id.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!video.signedUrl) {
      el.removeAttribute("src");
      el.load();
      setPlaying(false);
      return;
    }
    const wasPlaying = !el.paused && !!el.currentSrc;
    const resumeAt = Math.min(
      el.currentTime || watchedUpToRef.current || 0,
      watchedUpToRef.current || 0
    );
    el.src = video.signedUrl;
    el.load();
    const onLoaded = () => {
      const duration = el.duration || video.durationSeconds || 0;
      const endThreshold = 2;
      
      if (resumeAt > 0 && Number.isFinite(resumeAt)) {
        const tooCloseToEnd = duration > 0 && (duration - resumeAt) < endThreshold;
        
        if (tooCloseToEnd) {
          seekingProgrammaticallyRef.current = true;
          el.currentTime = 0;
          setTimeout(() => {
            seekingProgrammaticallyRef.current = false;
          }, 100);
        } else {
          try {
            seekingProgrammaticallyRef.current = true;
            el.currentTime = resumeAt;
            setTimeout(() => {
              seekingProgrammaticallyRef.current = false;
            }, 100);
          } catch {
            seekingProgrammaticallyRef.current = false;
          }
        }
      }
      if (wasPlaying) {
        void el.play().catch(() => setPlaying(false));
      }
    };
    el.addEventListener("loadedmetadata", onLoaded, { once: true });
    setPlaying(false);
    return () => el.removeEventListener("loadedmetadata", onLoaded);
  }, [video.signedUrl]);

  const notifyIfNeeded = useCallback(
    (data: VideoProgressResponse, wasCompleted: boolean) => {
      if (data.completed) {
        setCompleted(true);
      }
      const shouldNotify =
        data.lessonCompleted || (data.completed && !wasCompleted);
      if (shouldNotify && !notifiedLessonCompleteRef.current) {
        notifiedLessonCompleteRef.current = true;
        onCompleted(video.id);
      }
    },
    [onCompleted, video.id]
  );

  const sendProgress = useCallback(
    async (opts?: { ended?: boolean }) => {
      const el = videoRef.current;
      if (!el || !video.signedUrl) return;
      if (opts?.ended) pendingEndedRef.current = true;
      if (progressInFlightRef.current) return;
      progressInFlightRef.current = true;
      try {
        do {
          const sendEnded = pendingEndedRef.current;
          pendingEndedRef.current = false;
          try {
            const wasCompleted = completed;
            const { data } = await api.post<VideoProgressResponse>(
              `agent/courses/${courseId}/lessons/${lessonId}/videos/${video.id}/progress`,
              {
                currentTime: Math.floor(el.currentTime),
                ...(sendEnded ? { ended: true } : {}),
              }
            );
            watchedUpToRef.current = Math.max(
              watchedUpToRef.current,
              data.maxWatchedSeconds
            );
            notifyIfNeeded(data, wasCompleted);
          } catch {
            // silencioso; o próximo heartbeat tenta de novo
          }
        } while (pendingEndedRef.current);
      } finally {
        progressInFlightRef.current = false;
      }
      if (pendingEndedRef.current) {
        void sendProgress({ ended: true });
      }
    },
    [courseId, lessonId, video.id, video.signedUrl, completed, notifyIfNeeded]
  );

  const sendProgressRef = useRef(sendProgress);
  sendProgressRef.current = sendProgress;

  useEffect(() => {
    if (!playing) return;
    const interval = setInterval(
      () => void sendProgressRef.current(),
      HEARTBEAT_MS
    );
    return () => {
      clearInterval(interval);
      void sendProgressRef.current();
    };
  }, [playing]);

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    if (seekingProgrammaticallyRef.current) return;
    
    const gracePeriod = 500;
    const timeSincePlayStarted = Date.now() - playStartedAtRef.current;
    if (playStartedAtRef.current > 0 && timeSincePlayStarted < gracePeriod) {
      setCurrentTime(el.currentTime);
      return;
    }
    
    const t = el.currentTime;
    if (t <= watchedUpToRef.current + SEEK_TOLERANCE) {
      watchedUpToRef.current = Math.max(watchedUpToRef.current, t);
      setCurrentTime(t);
    } else {
      el.currentTime = watchedUpToRef.current;
    }
  };

  const handleSeeking = () => {
    const el = videoRef.current;
    if (!el) return;
    if (seekingProgrammaticallyRef.current) return;
    
    const gracePeriod = 500;
    const timeSincePlayStarted = Date.now() - playStartedAtRef.current;
    if (playStartedAtRef.current > 0 && timeSincePlayStarted < gracePeriod) {
      return;
    }
    
    if (el.currentTime > watchedUpToRef.current + SEEK_TOLERANCE) {
      el.currentTime = watchedUpToRef.current;
    }
  };

  const togglePlay = async () => {
    const el = videoRef.current;
    if (!el) return;
    
    if (el.paused) {
      try {
        await el.play();
        playStartedAtRef.current = Date.now();
        setPlaying(true);
      } catch (error) {
        console.error('Falha ao reproduzir vídeo:', error);
        setPlaying(false);
      }
    } else {
      el.pause();
      setPlaying(false);
      void sendProgress();
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    void sendProgress({ ended: true });
  };

  const handleVolumeChange = (newVolume: number) => {
    const el = videoRef.current;
    if (!el) return;
    setVolume(newVolume);
    el.volume = newVolume;
    if (newVolume > 0) {
      setMuted(false);
      el.muted = false;
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const newMuted = !muted;
    setMuted(newMuted);
    el.muted = newMuted;
  };

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    if (!container) return;

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleRateChange = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.playbackRate !== 1) {
      el.playbackRate = 1;
    }
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.addEventListener("ratechange", handleRateChange);
    return () => el.removeEventListener("ratechange", handleRateChange);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    
    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
    };
  }, []);

  const duration = video.durationSeconds || 0;
  const progressPct = duration
    ? Math.min(100, (watchedUpToRef.current / duration) * 100)
    : 0;

  const VolumeIcon = muted
    ? FaVolumeXmark
    : volume > 0.5
    ? FaVolumeHigh
    : FaVolumeLow;

  return (
    <div
      ref={containerRef}
      className="rounded-xl border border-gray-200 overflow-hidden bg-black shadow-lg"
    >
      <div className="relative bg-black">
        <video
          ref={videoRef}
          className="w-full aspect-video"
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onEnded={handleEnded}
          onContextMenu={(e) => e.preventDefault()}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          playsInline
        />
      </div>

      <div className="bg-gradient-to-t from-gray-900 to-gray-800 p-4">
        <div className="flex items-center gap-4 mb-3">
          <button
            type="button"
            onClick={togglePlay}
            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors shrink-0"
            aria-label={playing ? "Pausar" : "Reproduzir"}
          >
            {playing ? (
              <FaPause className="text-lg" />
            ) : (
              <FaPlay className="ml-0.5 text-lg" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate text-sm mb-1">
              {video.title}
            </p>
            <p className="text-xs text-gray-300 tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleMute}
                className="text-white hover:text-blue-400 transition-colors p-2"
                aria-label={muted ? "Ativar som" : "Silenciar"}
              >
                <VolumeIcon className="text-lg" />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={(e) => handleVolumeChange(Number(e.target.value))}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label="Volume"
              />
            </div>

            <button
              type="button"
              onClick={toggleFullscreen}
              className="text-white hover:text-blue-400 transition-colors p-2"
              aria-label={
                isFullscreen ? "Sair da tela cheia" : "Tela cheia"
              }
            >
              {isFullscreen ? (
                <FaCompress className="text-lg" />
              ) : (
                <FaExpand className="text-lg" />
              )}
            </button>
          </div>
        </div>

        <div className="w-full h-3 rounded-full bg-gray-700/90 overflow-hidden ring-1 ring-white/10">
          <div
            className="h-full rounded-full bg-preppoBlue transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {!completed && (
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
            <svg
              className="w-3 h-3"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            Não é possível adiantar ou acelerar o vídeo
          </p>
        )}
      </div>
    </div>
  );
};

export default CourseVideoPlayer;
